import axios from 'src/utils/axios';
import { NOTIFICATION_STATUS } from 'src/constants';
import { addNotification } from 'src/actions/notificationActions';

export const SET_CURRENT_MESSAGE = '@message/set-current-message';
export const SET_FILE_UPLOAD_TO_MESSAGE = '@message/set-file-upload-to-message';
export const SET_IS_MODAL_VISIBLE = '@message/set-modal-visible';
export const SET_IS_MODAL_LOADING = '@message/set-modal-loading';
export const GET_MESSAGES = '@message/get-messages';
export const GET_MESSAGES_SUCCESS = '@message/get-messages-success';
export const GET_MESSAGES_ERROR = '@message/get-messages-error';
export const UPDATE_MESSAGE = '@message/update-message';
export const CREATE_MESSAGE = '@message/create-message';
export const DELETE_MESSAGE = '@message/delete-message';
export const GET_USER_MESSAGE_CODES = '@message/get-user-message-codes';
export const SET_MESSAGE_NAVIGATION_TAB = '@message/set-message-navigation-tab';
export const SET_MESSAGES_QUERY = '@message/set-messages-query';

export const SET_IMPORT_PB_TEMPLATES_MODAL_VISIBLE = '@message/set-import-pb-templates-modal-visible';
export const GET_PB_TEMPLATES = '@message/get-pb-templates';
export const GET_PB_TEMPLATES_SUCCESS = '@message/get-pb-templates-success';
export const GET_PB_TEMPLATES_ERROR = '@message/get-pb-templates-error';
export const IMPORT_PB_TEMPLATES = '@message/import-pb-templates';
export const IMPORT_PB_TEMPLATES_SUCCESS = '@message/import-pb-templates-success';
export const IMPORT_PB_TEMPLATES_ERROR = '@message/import-pb-templates-error';
export const SET_SELECTED_PB_TEMPLATES = '@message/set-selected-pb-templates';

export const setCurrentMessage = (message) => ({
  type: SET_CURRENT_MESSAGE,
  payload: message
});

export const setFileUploadToMessage = (file) => ({
  type: SET_FILE_UPLOAD_TO_MESSAGE,
  payload: file
});

export const setIsModalVisible = () => ({
  type: SET_IS_MODAL_VISIBLE
});

export const setIsModalLoading = (loading) => ({
  type: SET_IS_MODAL_LOADING,
  payload: loading
});

export const setMessagesQuery = (query = {}) => ({
  type: SET_MESSAGES_QUERY,
  payload: query
});

export const setImportPBTemplatesModalVisible = (visible) => ({
  type: SET_IMPORT_PB_TEMPLATES_MODAL_VISIBLE,
  payload: visible
});

export const setSelectedPBTemplates = (templates) => ({
  type: SET_SELECTED_PB_TEMPLATES,
  payload: templates
});

export const getMessages = () => async (dispatch, getState) => {
  try {
    const { currentNavigationTab, query } = getState()?.messages || {};
    dispatch({
      type: GET_MESSAGES,
    });

    // Prepare request parameters
    const params = {
      currentNavigationTab,
    };

    // Add search parameter if present
    if (query?.search) {
      params.search = query.search;
    }

    // CON-1621 — scope filter; omitted (All) means no filter server-side
    if (query?.contextScope) {
      params.contextScope = query.contextScope;
    }

    const response = await axios.get('/api/followupmessages', {
      params,
    });

    dispatch({
      type: GET_MESSAGES_SUCCESS,
      payload: response.data
    });
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    const { message: errorMessage } = error || {};
    dispatch(addNotification(GET_MESSAGES_ERROR, message || errorMessage || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: GET_MESSAGES_ERROR,
    });
  }
};

export const getPBTemplates = () => async (dispatch) => {
  try {
    dispatch({
      type: GET_PB_TEMPLATES,
    });

    const response = await axios.get('/api/followupmessages/pb-templates');

    dispatch({
      type: GET_PB_TEMPLATES_SUCCESS,
      payload: response.data
    });
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    const { message: errorMessage } = error || {};
    dispatch(addNotification(GET_PB_TEMPLATES_ERROR, message || errorMessage || 'Failed to fetch PB templates', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: GET_PB_TEMPLATES_ERROR,
    });
  }
};

export const importPBTemplates = (selectedTemplates, doWhenTry = () => { }, doWhenCatch = () => { }) => async (dispatch) => {
  try {
    dispatch({
      type: IMPORT_PB_TEMPLATES,
    });

    const response = await axios.post('/api/followupmessages/import-pb-templates', {
      selectedTemplates
    });

    const {
      importedTemplates,
      templateIssues,
      templatesLength,
    } = response?.data || {};

    const importedTemplatesCount = importedTemplates?.length || 0;
    const templateIssuesCount = templateIssues?.length || 0;

    if (templateIssuesCount === templatesLength || importedTemplatesCount === 0) {
      dispatch(addNotification(IMPORT_PB_TEMPLATES_ERROR, `${templateIssuesCount} template${templateIssuesCount > 1 ? 's were' : ' was'} not imported from PB.`, NOTIFICATION_STATUS.ERROR));
      dispatch({
        type: IMPORT_PB_TEMPLATES_ERROR,
      });
      return;
    }

    if (importedTemplatesCount === templatesLength && templateIssuesCount === 0) {
      dispatch(addNotification(IMPORT_PB_TEMPLATES_SUCCESS, 'Templates were imported successfully from PB', NOTIFICATION_STATUS.SUCCESS));
      dispatch({
        type: IMPORT_PB_TEMPLATES_SUCCESS,
      });
      doWhenTry();
      return;
    }

    dispatch(addNotification(
      IMPORT_PB_TEMPLATES_SUCCESS,
      `${importedTemplatesCount} template${importedTemplatesCount > 1 ? 's were' : ' was'} imported successfully${templateIssuesCount > 0 ? ` and ${templateIssuesCount} template${templateIssuesCount > 1 ? 's were' : ' was'} not imported` : ''}`,
      templateIssuesCount > 0 ? NOTIFICATION_STATUS.WARNING : NOTIFICATION_STATUS.SUCCESS
    ));

    dispatch({
      type: IMPORT_PB_TEMPLATES_SUCCESS,
    });
    doWhenTry();
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    const { message: errorMessage } = error || {};
    dispatch(addNotification(IMPORT_PB_TEMPLATES_ERROR, message || errorMessage || 'Failed to import templates from PB', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: IMPORT_PB_TEMPLATES_ERROR,
    });
    doWhenCatch(e);
  }
};

export const getUserMessageCode = () => async (dispatch) => {
  try {
    dispatch(setIsModalLoading(true));
    const response = await axios.post('/api/followupmessages/used-code', {
      type: 'user',
    });
    dispatch({
      type: GET_USER_MESSAGE_CODES,
      payload: response.data
    });
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(GET_USER_MESSAGE_CODES, message || error?.message || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch(setIsModalLoading(false));
  }
};

export const createMessage = (messageData, doWhenTry = () => { }, doWhenCatch = () => { }) => async (dispatch) => {
  try {
    dispatch(setIsModalLoading(true));
    const response = await axios.post('/api/followupmessages', messageData);
    dispatch({
      type: CREATE_MESSAGE,
      payload: response?.data,
    });
    dispatch(addNotification(CREATE_MESSAGE, 'Message has been created', NOTIFICATION_STATUS.SUCCESS));
    dispatch(getUserMessageCode());
    dispatch(getMessages());
    doWhenTry();
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(CREATE_MESSAGE, message || error?.message || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch(setIsModalLoading(false));
    doWhenCatch(error, message);
  }
};

export const updateMessage = (messageChanges, doWhenTry = () => { }, doWhenCatch = () => { }) => async (dispatch) => {
  try {
    dispatch(setIsModalLoading(true));
    const response = await axios.put('/api/followupmessages', { messageChanges });
    dispatch({
      type: UPDATE_MESSAGE,
      payload: response?.data?.message,
    });
    dispatch(addNotification(UPDATE_MESSAGE, 'Message updated', NOTIFICATION_STATUS.SUCCESS));
    dispatch(getUserMessageCode());
    dispatch(getMessages());
    doWhenTry();
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(UPDATE_MESSAGE, message || error?.message || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch(setIsModalLoading(false));
    doWhenCatch(error, message);
  }
};

export const setMessageNavigationTab = (tabName) => async (dispatch) => {
  dispatch({
    type: SET_MESSAGE_NAVIGATION_TAB,
    payload: tabName
  });
};

export const deleteMessage = (messageId) => async (dispatch) => {
  try {
    dispatch(setIsModalLoading(true));
    await axios.delete(`/api/followupmessages/${messageId}`);
    dispatch({
      type: DELETE_MESSAGE,
    });
    dispatch(addNotification(DELETE_MESSAGE, 'Message has been deleted', NOTIFICATION_STATUS.SUCCESS));
    dispatch(getUserMessageCode());
    dispatch(getMessages());
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(DELETE_MESSAGE, message || error?.message || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch(setIsModalLoading(false));
  }
};

/**
 * CON-1625 — Message Template sharing.
 *
 * verify is read-only and returns the preview the confirm modal renders;
 * apply COPIES the template into this account with THIS user's next free
 * usr code. A re-apply of a code already in this account's lineage comes
 * back alreadyExists (matched by root provenance) rather than creating a
 * second copy, so the notification says so instead of claiming a new one.
 */
export const verifyMessageTemplateShareCode = (shareCode) => async () => {
  const { data } = await axios.post('/api/followupmessages/verifyShareCode', {
    params: { shareCode },
  });
  return data;
};

export const applyMessageTemplateShareCode = (shareCode) => async (dispatch) => {
  try {
    const { data } = await axios.post('/api/followupmessages/applyShareCode', {
      params: { shareCode },
    });

    dispatch(addNotification(
      CREATE_MESSAGE,
      data?.alreadyExists
        ? 'You already have this template, so nothing was added'
        : 'Message template has been added',
      data?.alreadyExists ? NOTIFICATION_STATUS.WARNING : NOTIFICATION_STATUS.SUCCESS,
    ));
    dispatch(getUserMessageCode());
    dispatch(getMessages());
    return data;
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      CREATE_MESSAGE,
      message
        || error?.message
        // The maintenance 503 carries its text in `error`, not `message`.
        || (e?.response?.status === 503 && typeof error === 'string' ? error : null)
        || 'Try again later',
      NOTIFICATION_STATUS.ERROR,
    ));
    return null;
  }
};

export const duplicateMessageTemplate = (id) => async (dispatch) => {
  try {
    const { data } = await axios.post(`/api/followupmessages/${id}/duplicate`);
    dispatch(addNotification(CREATE_MESSAGE, 'Message template has been duplicated', NOTIFICATION_STATUS.SUCCESS));
    dispatch(getUserMessageCode());
    dispatch(getMessages());
    return data;
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      CREATE_MESSAGE,
      message || error?.message || 'Try again later',
      NOTIFICATION_STATUS.ERROR,
    ));
    return null;
  }
};
